import { Icon, cn } from "@sagot/ui";
import { selectedChipClass, useChatTheme } from "../components/chat-theme";
import type { Slot } from "./contract";
import { formatDayChip, formatDayLong, formatTime } from "./dates";
import type { ActionLabels } from "./labels";
import { ROVING_GROUP_ATTRIBUTE, ROVING_ITEM_ATTRIBUTE, moveRovingFocus } from "./roving";
import type { SlotDay } from "./use-slots";

const CHIP_BASE =
  "inline-flex min-h-11 min-w-11 shrink-0 cursor-pointer items-center justify-center gap-1 rounded-md border px-3 py-1 text-sm font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-50";
const CHIP_IDLE = "border-line-strong bg-surface text-fg hover:border-primary hover:bg-primary-soft";

const rovingProps = { [ROVING_ITEM_ATTRIBUTE]: "" };
const groupProps = { [ROVING_GROUP_ATTRIBUTE]: "" };

export interface DayChipsProps {
  days: readonly SlotDay[];
  activeDate: string;
  locale?: string;
  labels: ActionLabels;
  disabled?: boolean;
  onSelect: (date: string) => void;
}

/** One chip per day with open times; one tab stop (the active day), arrow keys move between days. */
export function DayChips({ days, activeDate, locale, labels, disabled, onSelect }: DayChipsProps) {
  const { themed } = useChatTheme();
  return (
    <div role="group" aria-label={labels.chooseDay} className="-mx-1 flex gap-2 overflow-x-auto px-1 pb-1" {...groupProps}>
      {days.map((day) => {
        const text = formatDayChip(day.date, locale);
        const active = day.date === activeDate;
        return (
          <button
            key={day.date}
            type="button"
            aria-pressed={active}
            aria-label={labels.dayChip(`${text.weekday}, ${text.date}`, day.slots.length)}
            tabIndex={active ? 0 : -1}
            disabled={disabled}
            onClick={() => {
              onSelect(day.date);
            }}
            onKeyDown={moveRovingFocus}
            className={cn(CHIP_BASE, "min-w-16 flex-col gap-0 leading-tight", active ? selectedChipClass(themed) : CHIP_IDLE)}
            {...rovingProps}
          >
            <span aria-hidden="true" className="text-[11px] font-medium uppercase">
              {text.weekday}
            </span>
            <span aria-hidden="true">{text.date}</span>
          </button>
        );
      })}
    </div>
  );
}

export interface TimeChipsProps {
  date: string;
  slots: readonly Slot[];
  selectedId: string | null;
  timeZone: string;
  locale?: string;
  labels: ActionLabels;
  disabled?: boolean;
  onSelect: (slot: Slot) => void;
}

/** Times of one day in the visitor's zone; the selected chip is `aria-pressed` and carries a check mark. */
export function TimeChips({ date, slots, selectedId, timeZone, locale, labels, disabled, onSelect }: TimeChipsProps) {
  const { themed } = useChatTheme();
  const tabStop = slots.find((slot) => slot.id === selectedId)?.id ?? slots[0]?.id;
  return (
    <div role="group" aria-label={`${labels.chooseTime} — ${formatDayLong(date, locale)}`} className="grid grid-cols-3 gap-2 sm:grid-cols-4" {...groupProps}>
      {slots.map((slot) => {
        const selected = slot.id === selectedId;
        return (
          <button
            key={slot.id}
            type="button"
            aria-pressed={selected}
            tabIndex={slot.id === tabStop ? 0 : -1}
            disabled={disabled}
            onClick={() => {
              onSelect(slot);
            }}
            onKeyDown={moveRovingFocus}
            className={cn(CHIP_BASE, selected ? selectedChipClass(themed) : CHIP_IDLE)}
            {...rovingProps}
          >
            {selected ? <Icon name="check" className="size-3.5" /> : null}
            {formatTime(slot.start, timeZone, locale)}
          </button>
        );
      })}
    </div>
  );
}
